/*
 * ContactsContext — single-fetch cache for `/api/settings/contacts`.
 *
 * The site footer is remounted on every route change. Fetching the
 * contacts endpoint inside the footer caused a race window where the
 * new footer briefly rendered empty (or hidden by the 300ms enter
 * animation) before the API call resolved. Moving the fetch up to
 * the app root means every page that mounts a footer reads the same
 * already-resolved value — no refetch, no empty flash.
 *
 * A `FALLBACK_CONTACTS` shape guarantees the footer always has
 * something sensible to render even if the network fails, the
 * backend is down, or the cache is being warmed up.
 *
 * The fetch re-runs when `lang` changes so IT/EN switches still
 * reload localized contact strings.
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useLang } from '../lib/lang';

export const FALLBACK_CONTACTS = {
    company_name: 'ARAIA ALL DESIGN',
    address: 'Strà, Venezia (IT)',
    email: 'info@alldesignsrl.com',
    // iter69·e — `phone` rimosso dal fallback: il numero non deve MAI
    // apparire nell'UI del sito, nemmeno come default.
    socials: [
        { platform: 'Instagram', url: 'https://www.instagram.com/all_design_srl' },
        {
            platform: 'LinkedIn',
            url: 'https://it.linkedin.com/in/all-design-srl-footwear-design-e-product-development-8b0ba5199',
        },
    ],
};

const ContactsContext = createContext({ contacts: FALLBACK_CONTACTS, loading: false });

export function ContactsProvider({ children }) {
    const lang = useLang();
    const [contacts, setContacts] = useState(FALLBACK_CONTACTS);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        api.getContacts(lang)
            .then((data) => {
                if (!alive) return;
                // Merge fetched values on top of fallback so any missing
                // key still resolves to a safe default.
                setContacts({ ...FALLBACK_CONTACTS, ...(data || {}) });
                setLoading(false);
            })
            .catch(() => {
                if (!alive) return;
                // Keep the fallback shape — the footer stays readable.
                setLoading(false);
            });
        return () => {
            alive = false;
        };
    }, [lang]);

    return (
        <ContactsContext.Provider value={{ contacts, loading }}>
            {children}
        </ContactsContext.Provider>
    );
}

export function useContacts() {
    return useContext(ContactsContext);
}
